import React, { useState } from 'react';
import { Routes, Route, useNavigate, Link as RouterLink } from 'react-router-dom';
import ImageUpload from './components/ImageUpload';
import ImageSearch from './components/ImageSearch';
import ReceiptReview from './components/ReceiptReview';
import DashboardPage from './components/DashboardPage';
import ReceiptDetailPage from './components/ReceiptDetailPage'; // NEW
import FuelDashboardPage from './components/FuelDashboardPage';

import {
  Container,
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Paper,
  Alert,
  TextField,
  Stack,
  IconButton,
  Grid
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';

const HomePage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mt: 2, textAlign: 'center' }}>
      <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
        Groceries AI
      </Typography>

      <Box component="form" onSubmit={handleSearch} sx={{ mt: 4, mb: 2, display: 'flex', justifyContent: 'center' }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search your groceries..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ maxWidth: 800 }}
          InputProps={{
            endAdornment: (
              <Stack direction="row" sx={{ ml: 1 }}>
                <IconButton type="submit" color="primary" aria-label="text search">
                  <SearchIcon />
                </IconButton>
                <IconButton color="primary" aria-label="image search" onClick={() => navigate('/search')}>
                  <PhotoCameraIcon />
                </IconButton>
              </Stack>
            )
          }}
        />
      </Box>

      <Grid container spacing={5} justifyContent="center">
        <Grid item size={{ xs: 4 }}>
          <Button fullWidth startIcon={<CloudUploadIcon />} onClick={() => navigate('/upload')}>
            Upload
          </Button>
        </Grid>
        <Grid item size={{ xs: 4 }}>
          <Button fullWidth startIcon={<LeaderboardIcon />} onClick={() => navigate('/stats')}>
            Stats
          </Button>
        </Grid>
        <Grid item size={{ xs: 4 }}>
          <Button fullWidth startIcon={<LocalGasStationIcon />} onClick={() => navigate('/fuel')}>
            Fuel
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

const PageWrapper = ({ children, title, showBackButton = false }) => {
  const navigate = useNavigate();
  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      {showBackButton && (
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')} sx={{ mb: 2 }}>
          Back to Home
        </Button>
      )}
      {title && (
        <Typography variant="h5" component="h2" gutterBottom>
          {title}
        </Typography>
      )}
      {children}
    </Paper>
  );
};


function App() {
  const [extractedReceiptData, setExtractedReceiptData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentError, setCurrentError] = useState(null);
  const [userMessage, setUserMessage] = useState('');
  const navigate = useNavigate();

  const resetMessages = () => {
    setCurrentError(null);
    setUserMessage('');
  };

  const handleUploadSuccess = (data) => {
    setExtractedReceiptData(data);
    setIsProcessing(false);
    resetMessages();
    setUserMessage('Receipt data extracted. Please review and approve.');
  };

  const handleUploadError = (errorMessage) => {
    setCurrentError(errorMessage);
    setIsProcessing(false);
    setExtractedReceiptData(null);
  };

  const handleProcessing = () => {
    setIsProcessing(true);
    resetMessages();
    setUserMessage('Processing receipt...');
  };

  const handleReviewApproval = async (reviewedData) => {
    setIsProcessing(true);
    setUserMessage('Saving your reviewed receipt data...');
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000/api';
    try {
      const response = await fetch(`${backendUrl}/save-processed-receipt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reviewedData),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'An unknown error occurred.' }));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      setExtractedReceiptData(null);
      setUserMessage(result.message || 'Receipt saved successfully!');
      navigate('/');
    } catch (error) {
      console.error('Failed to save reviewed data:', error);
      setCurrentError(error.message || 'Failed to save reviewed receipt.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReviewCancel = () => {
    setExtractedReceiptData(null);
    resetMessages();
    setUserMessage('Review cancelled.');
  };

  const renderMessages = () => (
    <Box sx={{ width: '100%', mb: 2, minHeight: '60px' }}>
      {userMessage && !currentError && (
         <Alert severity={isProcessing ? "info" : "success"} onClose={() => setUserMessage('')}>
            {userMessage}
        </Alert>
      )}
      {currentError && (
        <Alert severity="error" onClose={() => setCurrentError(null)}>
          Error: {currentError}
        </Alert>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static">
        <Toolbar>
          <ReceiptLongIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component={RouterLink} to="/" sx={{ flexGrow: 1, color: 'inherit', textDecoration: 'none' }}>
            Groceries AI
          </Typography>
        </Toolbar>
      </AppBar>

      <Container component="main" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
        {renderMessages()}
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/stats" element={
            <PageWrapper title="Spending Stats" showBackButton>
              <DashboardPage />
            </PageWrapper>
          } />
          <Route path="/search" element={
            <PageWrapper title="Search Your Groceries" showBackButton>
              <ImageSearch />
            </PageWrapper>
          } />
          <Route path="/fuel" element={
            <PageWrapper title="Fuel Price Trends" showBackButton>
              <FuelDashboardPage />
            </PageWrapper>
          } />
          <Route path="/upload" element={
            <PageWrapper title={!extractedReceiptData ? "Upload New Receipt" : "Review Receipt Data"} showBackButton>
              {!extractedReceiptData ? (
                <ImageUpload
                  onUploadSuccess={handleUploadSuccess}
                  onUploadError={handleUploadError}
                  onProcessing={handleProcessing}
                />
              ) : (
                <ReceiptReview
                  receiptData={extractedReceiptData}
                  onApprove={handleReviewApproval}
                  onCancel={handleReviewCancel}
                />
              )}
            </PageWrapper>
          } />
          <Route path="/history/:date" element={
            <PageWrapper showBackButton>
              <ReceiptDetailPage />
            </PageWrapper>
          } />
        </Routes>
      </Container>

      <Box component="footer" sx={{ py: 2, px: 2, mt: 'auto', backgroundColor: (theme) => theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[800], textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Groceries AI &copy; {new Date().getFullYear()}
        </Typography>
        {process.env.REACT_APP_BUILD_DATE && (
          <Typography variant="caption" color="text.secondary" display="block">
            Build {process.env.REACT_APP_BUILD_DATE}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default App;
